import { useQueryClient } from "@tanstack/react-query"
import { useRealtimeChannelEvent } from "@/contexts/RealtimeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import type { CommentableEntity } from "../services/comment.api"

interface CommentRealtimeEvent {
  entityType?: "COMMUNITY" | "RITUAL"
  entityId?: string
}

const normalizeEntityType = (entityType: CommentableEntity): "COMMUNITY" | "RITUAL" =>
  entityType === "community" ? "COMMUNITY" : "RITUAL"

export const useCommentRealtime = ({
  entityType,
  entityId,
  enabled = true,
}: {
  entityType: CommentableEntity
  entityId: string
  enabled?: boolean
}): void => {
  const queryClient = useQueryClient()
  const userId = useAuthStore(state => state.user?.id)
  const expectedEntityType = normalizeEntityType(entityType)

  // Discussion events are fanned out to the recipient's own identity channel;
  // we listen there and filter by entity. Backend grants the discussion ping
  // only to recipients with read access, so simply receiving an event is the
  // authorization signal (the REST refetch remains the authoritative gate).
  const channelName = enabled && entityId && userId ? `user:${userId}` : null

  useRealtimeChannelEvent(channelName, "discussion:changed", payload => {
    const event = payload as CommentRealtimeEvent

    if (event.entityId !== entityId || event.entityType !== expectedEntityType) {
      return
    }

    void queryClient.invalidateQueries({
      queryKey: ["comments", entityType, entityId],
    })

    if (entityType === "community") {
      void queryClient.invalidateQueries({
        queryKey: ["community-statistics", entityId],
      })
    }
  })
}
